import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateUser, clearUser } from '../feature/user.Slice';
import OptIn from '../components/Optin';
import { downloadJsonAsExcel } from '../utils/excel';
import { QRCodeCanvas } from 'qrcode.react';

const regexEmail = /^[^@\s]{2,30}@[^\s@]{2,30}\.[^@\s]{2,}$/;
const regexPassword = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*\-+.]).{8,}$/;

const apiPrefix = process.env.REACT_APP_API_PREFIX;

/**
 * @file
 * @module Pages/Profile
 * @description Profile est une page qui permet à un utilisateur de consulter et de modifier son profil.
 * - L'utilisateur peut modifier son nom d'utilisateur, son mot de passe, son prénom et son nom de famille.
 * - Cette page utilise le hook useEffect de React pour appeler l'API lors du chargement de la page.
 * - Cette page utilise le hook useSelector de 'react-redux' pour accéder aux informations de l'utilisateur connecté.
 * - Si lui-même est un chef d'entreprise, il peut également voir les informations de ses employés.
 * et exporter les informations de ses employés en format CSV.
 */

const Profile = () => {
    const [selectedTab, setSelectedTab] = useState('profile');
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user.user.user);
    const token = useSelector((state) => state.user.token);
    const optInStatus = user?.optin;
    const [error, setError] = useState('');
    const [username, setUsername] = useState(user.username);
    const [password, setPassword] = useState('');
    const [firstname, setFirstname] = useState(user.firstname);
    const [lastname, setLastname] = useState(user.lastname);
    const [company] = useState(user.company);
    const [siret] = useState(user.siret);
    const [users, setUsers] = useState([]);
    const [signupUrl, setSignupUrl] = useState('');
    const urlRef = useRef(null);
    const qrCodeRef = useRef(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [usersToDelete, setUsersToDelete] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [emailChangeStep, setEmailChangeStep] = useState(null);
    const [emailChangeCode, setEmailChangeCode] = useState('');
    const [emailChangePending, setEmailChangePending] = useState('');
    const [emailChangeMessage, setEmailChangeMessage] = useState('');
    const [emailChangeError, setEmailChangeError] = useState('');
    const [emailChangeLoading, setEmailChangeLoading] = useState(false);

    useEffect(() => {
        if (selectedTab === 'other') {
            const fetchData = async () => {
                try {
                    const response = await fetch(apiPrefix + 'users/filter', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ siret, company }),
                    });

                    if (response.ok) {
                        const data = await response.json();
                        setUsers(data);
                    } else {
                        console.error('Erreur lors de la récupération des utilisateurs', response.statusText);
                    }
                } catch (error) {
                    console.error('Erreur lors de la récupération des utilisateurs', error);
                }
            };

            fetchData();

            if (company && siret && token) {
                fetch(apiPrefix + 'signup-link', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({}),
                })
                    .then((response) => response.ok ? response.json() : null)
                    .then((data) => {
                        if (data?.url) setSignupUrl(data.url);
                    })
                    .catch((error) => {
                        console.error('Erreur lors de la création du lien d\'inscription', error);
                    });
            }
        }
    }, [selectedTab, siret, company, token]);

    useEffect(() => {
        setUsername(user.username);
        setFirstname(user.firstname);
        setLastname(user.lastname);
    }, [user]);

    const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };

    const startEmailChange = async (newEmail) => {
        setEmailChangeLoading(true);
        setEmailChangeError('');
        try {
            const response = await fetch(apiPrefix + 'account/email-change/request', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ newEmail })
            });
            const data = await response.json();
            if (!response.ok) {
                setError(data.error || 'Impossible de démarrer le changement d\'email.');
                return;
            }
            setEmailChangePending(data.pendingEmail || newEmail);
            setEmailChangeStep(data.step);
            setEmailChangeMessage(data.message);
            setEmailChangeCode('');
        } catch (err) {
            console.error(err);
            setError('Impossible de démarrer le changement d\'email.');
        } finally {
            setEmailChangeLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        const emailChanged = username.trim().toLowerCase() !== String(user.username || '').trim().toLowerCase();
        const profileChanged = firstname !== user.firstname || lastname !== user.lastname || Boolean(password);

        if (emailChanged && !regexEmail.test(username.trim())) {
            setError("Le nom d'utilisateur doit être une adresse e-mail valide.");
            return;
        }

        if (profileChanged && !regexPassword.test(password)) {
            setError("Le mot de passe doit contenir au moins 8 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial.");
            return;
        }

        if (!emailChanged && !profileChanged) {
            return;
        }

        setError('');
        setSuccessMessage('');

        if (profileChanged) {
            const data = { password, firstname, lastname };

            fetch(apiPrefix + `update/${user.id}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(data)
            })
            .then(response => response.json())
            .then(data => {
                if (data.message === 'Mise à jour réussie') {
                    dispatch(updateUser({
                        ...user,
                        firstname,
                        lastname
                    }));
                    if (!emailChanged) {
                        setSuccessMessage('Profil mis à jour.');
                    }
                } else if (data.error) {
                    setError(data.error);
                }
            })
            .catch((error) => {
                console.error('Error:', error);
                dispatch(clearUser());
            });
        }

        if (emailChanged) {
            await startEmailChange(username.trim());
        }
    };

    const closeEmailChangeModal = () => {
        setEmailChangeStep(null);
        setEmailChangeCode('');
        setEmailChangeError('');
        setEmailChangeMessage('');
        setEmailChangePending('');
    };

    const handleVerifyEmailCode = async (e) => {
        e.preventDefault();
        setEmailChangeLoading(true);
        setEmailChangeError('');
        try {
            const response = await fetch(apiPrefix + 'account/email-change/verify', {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ code: emailChangeCode })
            });
            const data = await response.json();
            if (!response.ok) {
                setEmailChangeError(data.error || 'Code invalide.');
                return;
            }

            if (data.step === 'completed') {
                dispatch(updateUser({
                    ...user,
                    username: data.username
                }));
                setUsername(data.username);
                closeEmailChangeModal();
                setSuccessMessage(data.message || 'Votre adresse email a été mise à jour.');
                return;
            }

            setEmailChangeStep(data.step);
            setEmailChangePending(data.pendingEmail || emailChangePending);
            setEmailChangeMessage(data.message);
            setEmailChangeCode('');
        } catch (err) {
            console.error(err);
            setEmailChangeError('Une erreur est survenue lors de la vérification.');
        } finally {
            setEmailChangeLoading(false);
        }
    };

    const handleResendEmailCode = async () => {
        setEmailChangeLoading(true);
        setEmailChangeError('');
        try {
            const response = await fetch(apiPrefix + 'account/email-change/resend', {
                method: 'POST',
                headers: authHeaders
            });
            const data = await response.json();
            if (!response.ok) {
                setEmailChangeError(data.error || 'Impossible de renvoyer le code.');
                return;
            }
            setEmailChangeMessage(data.message);
        } catch (err) {
            console.error(err);
            setEmailChangeError('Impossible de renvoyer le code.');
        } finally {
            setEmailChangeLoading(false);
        }
    };

    const handleCancelEmailChange = async () => {
        setEmailChangeLoading(true);
        try {
            await fetch(apiPrefix + 'account/email-change/cancel', {
                method: 'POST',
                headers: authHeaders
            });
        } catch (err) {
            console.error(err);
        } finally {
            setEmailChangeLoading(false);
            closeEmailChangeModal();
        }
    };

    const formatLastActive = (lastActive) => {
        return !lastActive || new Date(lastActive).getFullYear() === 1970
            ? 'Pas d\'activité'
            : new Date(lastActive).toLocaleString();
    };

    const handleCopyUrl = async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(signupUrl);
            } else {
                // Fallback pour les navigateurs plus anciens
                urlRef.current.select();
                document.execCommand('copy');
            }
        } catch (error) {
            console.error('Erreur lors de la copie:', error);
            // Fallback si l'API clipboard échoue
            urlRef.current.select();
            document.execCommand('copy');
        }
    };

    const getQrFilename = () => {
        const safeName = (company || 'entreprise')
            .trim()
            .replace(/[/\\?%*:|"<>]/g, '')
            .replace(/\s+/g, '-');
        return `inscription-${safeName}.png`;
    };

    const handleDownloadQrCode = () => {
        const canvas = qrCodeRef.current;
        if (!canvas) return;

        const pngUrl = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = getQrFilename();
        downloadLink.click();
    };


    const exportToExcel = async () => {
        const fieldsToExport = [
            'id',
            'username',
            'firstname',
            'lastname',
            'lastactive',
            'company',
            'siret',
            'creationDate'
    
        ];
    
        const filteredUsers = users.map(user => {
            const filtered = {};
            fieldsToExport.forEach(field => {
                filtered[field] = user[field];
            });
            return filtered;
        });
    
        await downloadJsonAsExcel(filteredUsers, 'Utilisateurs', 'users.xlsx');
    };

    const handleOpenDeleteModal = () => {
        setSelectedUsers([]);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedUsers([]);
    };

    const handleToggleUser = (userId) => {
        setSelectedUsers(prev => 
            prev.includes(userId) 
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSelectAll = () => {
        if (selectedUsers.length === users.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(users.map(u => u.id));
        }
    };

    const handleValidateSelection = () => {
        const selected = users.filter(u => selectedUsers.includes(u.id));
        setUsersToDelete(selected);
        setShowDeleteModal(false);
        setShowConfirmModal(true);
    };

    const handleCloseConfirmModal = () => {
        setShowConfirmModal(false);
        setUsersToDelete([]);
        setSelectedUsers([]);
    };

    const handleConfirmDeletion = async () => {
        setIsSubmitting(true);
        try {
            const response = await fetch(apiPrefix + 'request-account-deletion', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    chefEntreprise: {
                        name: `${user.firstname} ${user.lastname}`,
                        email: user.username,
                        company: user.company,
                        siret: user.siret
                    },
                    usersToDelete: usersToDelete.map(u => ({
                        id: u.id,
                        email: u.username,
                        firstname: u.firstname,
                        lastname: u.lastname,
                        company: u.company,
                        siret: u.siret,
                        creationDate: u.creationDate,
                        lastactive: u.lastactive
                    }))
                })
            });

            if (response.ok) {
                setShowConfirmModal(false);
                setUsersToDelete([]);
                setSelectedUsers([]);
                // Optionnel : recharger la liste des utilisateurs
                // fetchData();
            } else {
                const error = await response.json();
                console.error('Erreur lors de l\'envoi de la demande:', error);
                alert('Une erreur est survenue lors de l\'envoi de la demande.');
            }
        } catch (error) {
            console.error('Erreur lors de l\'envoi de la demande:', error);
            alert('Une erreur est survenue lors de l\'envoi de la demande.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="container-responsive section-padding">
            <div className="flex border-b border-gray-200 mb-6">
                <div
                    className={`px-6 py-3 cursor-pointer font-medium transition-colors duration-200 ${
                        selectedTab === 'profile' 
                            ? 'text-primary border-b-2 border-primary' 
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setSelectedTab('profile')}
                >
                    Profil
                </div>
                {user.chefEntreprise && (
                    <div
                        className={`px-6 py-3 cursor-pointer font-medium transition-colors duration-200 ${
                            selectedTab === 'other' 
                                ? 'text-primary border-b-2 border-primary' 
                                : 'text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => setSelectedTab('other')}
                    >
                        Accès chef d'Entreprise
                    </div>
                )}
            </div>

            {selectedTab === 'profile' && (
                <div className="space-y-8">
                    <div className="card">
                        <h1 className="card-title mb-4">Profil</h1>
                        <div className="space-y-3">
                            <p className="flex items-center text-gray-700">
                                <i className="fas fa-user mr-3 text-primary"></i> 
                                <span className="font-medium">Nom d'utilisateur</span> : {user.firstname}
                            </p>
                            <p className="flex items-center text-gray-700">
                                <i className="fas fa-user-tag mr-3 text-primary"></i> 
                                <span className="font-medium">Prénom</span> : {user.lastname}
                            </p>
                            <p className="flex items-center text-gray-700">
                                <i className="fas fa-envelope mr-3 text-primary"></i> 
                                <span className="font-medium">Email</span> : {user.username}
                            </p>
                            <p className="flex items-center text-gray-700">
                                <i className="fas fa-building mr-3 text-primary"></i> 
                                <span className="font-medium">Société</span> : {user.company}
                            </p>
                            <p className="flex items-center text-gray-700">
                                <i className="fas fa-id-card mr-3 text-primary"></i> 
                                <span className="font-medium">Siret</span> : {user.siret}
                            </p>
                        </div>
                    </div>
                    <div className="card">
                        <h1 className="card-title mb-2">Abonnez-vous à notre Newsletter</h1>
                        <p className="text-gray-600 mb-4">Recevez les dernières nouvelles et mises à jour directement dans votre boîte de réception.</p>
                        <OptIn username={user.username} optIn={optInStatus} />
                    </div>
                    <div className="card">
                        <h1 className="card-title mb-2">Modifier mon profil</h1>
                        <p className="text-gray-600 mb-4">Les champs marqués d'un astérisque (*) sont obligatoires</p>
                        {error && <div className="alert alert-error mb-4">{error}</div>}
                        {successMessage && <div className="alert alert-info mb-4">{successMessage}</div>}
                        <form onSubmit={handleUpdate} className="space-y-4">
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="fas fa-envelope mr-2.5 text-primary"></i> 
                                    <span>Email</span>
                                </label>
                                <input 
                                    type="email" 
                                    value={username} 
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="form-input"
                                />
                                <p className="text-sm text-gray-500 mt-1">
                                    Un code sera envoyé à votre adresse actuelle, puis à la nouvelle, avant tout changement.
                                </p>
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="fas fa-key mr-2.5 text-primary"></i> 
                                    <span>Mot de passe {username.trim().toLowerCase() === String(user.username || '').trim().toLowerCase() ? '*' : ''}</span>
                                </label>
                                <input
                                    type="password"
                                    value={password}
                                    placeholder='******'
                                    onChange={(e) => setPassword(e.target.value)}
                                    required={username.trim().toLowerCase() === String(user.username || '').trim().toLowerCase()}
                                    className="form-input"
                                /> 
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="fas fa-user-tag mr-2.5 text-primary"></i> 
                                    <span>Prénom</span>
                                </label>
                                <input 
                                    type="text" 
                                    value={firstname} 
                                    onChange={(e) => setFirstname(e.target.value)}
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    <i className="fas fa-user mr-2.5 text-primary"></i> 
                                    <span>Nom de famille</span>
                                </label>
                                <input 
                                    type="text" 
                                    value={lastname} 
                                    onChange={(e) => setLastname(e.target.value)}
                                    className="form-input"
                                />
                            </div>
                            <button 
                                type="submit" 
                                disabled={emailChangeLoading || (!password && username.trim().toLowerCase() === String(user.username || '').trim().toLowerCase())}
                                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {emailChangeLoading ? 'Envoi en cours...' : 'Mettre à jour'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {selectedTab === 'other' && (
                <div className="space-y-8">
                    <div className="card">
                        <div className="flex justify-between items-center mb-4">
                            <h1 className="card-title mb-0">Ma Société</h1>
                            <button 
                                onClick={handleOpenDeleteModal}
                                className="btn-secondary"
                            >
                                <i className="fas fa-user-times mr-2"></i>
                                Demander la suppression de comptes
                            </button>
                        </div>
                        
                        {signupUrl && (
                            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                                    <i className="fas fa-link mr-2 text-primary"></i>
                                    URL d'inscription
                                </h2>
                                <p className="text-sm text-gray-600 mb-3">
                                    Partagez cette URL ou le QR code avec vos employés pour qu'ils puissent créer leur compte. 
                                    Les informations de votre entreprise seront automatiquement pré-remplies.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 items-start">
                                    <div className="flex gap-2 flex-1 w-full min-w-0">
                                        <input
                                            ref={urlRef}
                                            value={signupUrl}
                                            readOnly
                                            className="form-input flex-1 font-mono text-sm min-w-0"
                                        />
                                        <button 
                                            className="btn-secondary shrink-0"
                                            onClick={handleCopyUrl}
                                        >
                                            <i className="fas fa-copy mr-2"></i>
                                            Copier
                                        </button>
                                    </div>
                                    <div className="flex flex-col items-center gap-2 shrink-0 p-3 bg-white border border-blue-200 rounded-lg">
                                        <QRCodeCanvas
                                            ref={qrCodeRef}
                                            value={signupUrl}
                                            size={120}
                                            level="M"
                                            includeMargin
                                        />
                                        <button
                                            className="btn-secondary text-sm"
                                            onClick={handleDownloadQrCode}
                                        >
                                            <i className="fas fa-download mr-2"></i>
                                            Télécharger
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {users.map((user) => (
                                <div key={user.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    <div className="space-y-2">
                                        <p className="flex items-center text-gray-700">
                                            <i className="fas fa-user mr-3 text-primary"></i> 
                                            <span className="font-medium">{user.firstname} {user.lastname}</span>
                                        </p>
                                        <p className="flex items-center text-gray-700">
                                            <i className="fas fa-envelope mr-3 text-primary"></i> 
                                            <span>{user.username}</span>
                                        </p>
                                        <p className="flex items-center text-gray-700">
                                            <i className="fas fa-building mr-3 text-primary"></i> 
                                            <span>{user.company}</span>
                                        </p>
                                        <p className="flex items-center text-gray-700">
                                            <i className="fas fa-clock mr-3 text-primary"></i> 
                                            <span>{formatLastActive(user.lastactive)}</span>
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-6">
                            <button 
                                onClick={exportToExcel}
                                className="btn-secondary"
                            >
                                Exporter les utilisateurs en Excel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de sélection des comptes à supprimer */}
            {showDeleteModal && (
                <div className="modal-overlay" onClick={handleCloseDeleteModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button 
                            className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                            onClick={handleCloseDeleteModal}
                        >
                            ×
                        </button>
                        <div>
                            <h2 className="text-2xl font-bold text-primary mb-4">
                                <i className="fas fa-user-times mr-2"></i>
                                Sélectionner les comptes à supprimer
                            </h2>
                            <p className="text-gray-600 mb-4">
                                Cochez les comptes pour lesquels vous souhaitez demander la suppression.
                            </p>
                            
                            <div className="mb-4">
                                <button 
                                    onClick={handleSelectAll}
                                    className="btn-secondary text-sm"
                                >
                                    {selectedUsers.length === users.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                                </button>
                                <span className="ml-4 text-gray-600">
                                    {selectedUsers.length} compte(s) sélectionné(s) sur {users.length}
                                </span>
                            </div>

                            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 mb-4">
                                {users.length === 0 ? (
                                    <p className="text-gray-500 text-center py-4">Aucun utilisateur trouvé</p>
                                ) : (
                                    <div className="space-y-2">
                                        {users.map((user) => (
                                            <label 
                                                key={user.id} 
                                                className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(user.id)}
                                                    onChange={() => handleToggleUser(user.id)}
                                                    className="mr-3 w-4 h-4 text-primary"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-medium text-gray-800">
                                                        {user.firstname} {user.lastname}
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        {user.username}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Dernière activité : {formatLastActive(user.lastactive)}
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button 
                                    onClick={handleCloseDeleteModal}
                                    className="btn-secondary"
                                >
                                    Annuler
                                </button>
                                <button 
                                    onClick={handleValidateSelection}
                                    disabled={selectedUsers.length === 0}
                                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Valider la sélection ({selectedUsers.length})
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmation */}
            {showConfirmModal && (
                <div className="modal-overlay" onClick={handleCloseConfirmModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button 
                            className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                            onClick={handleCloseConfirmModal}
                        >
                            ×
                        </button>
                        <div>
                            <h2 className="text-2xl font-bold text-primary mb-4">
                                <i className="fas fa-exclamation-triangle mr-2 text-yellow-500"></i>
                                Confirmation de la demande de suppression
                            </h2>
                            <p className="text-gray-700 mb-4">
                                Vous êtes sur le point de demander la suppression de <strong>{usersToDelete.length}</strong> compte(s).
                            </p>
                            <p className="text-gray-600 mb-4">
                                Cette demande sera envoyée à notre équipe qui procédera à la suppression des comptes sélectionnés.
                            </p>

                            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                                <h3 className="font-semibold text-gray-800 mb-3">Récapitulatif des comptes à supprimer :</h3>
                                <div className="space-y-2">
                                    {usersToDelete.map((user) => (
                                        <div key={user.id} className="p-2 bg-white rounded border border-gray-200">
                                            <div className="font-medium text-gray-800">
                                                {user.firstname} {user.lastname}
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                {user.username}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button 
                                    onClick={handleCloseConfirmModal}
                                    className="btn-secondary"
                                    disabled={isSubmitting}
                                >
                                    Retour
                                </button>
                                <button 
                                    onClick={handleConfirmDeletion}
                                    disabled={isSubmitting}
                                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin mr-2"></i>
                                            Envoi en cours...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-paper-plane mr-2"></i>
                                            Confirmer et envoyer
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {emailChangeStep && (
                <div className="modal-overlay" onClick={handleCancelEmailChange}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
                            onClick={handleCancelEmailChange}
                            type="button"
                        >
                            ×
                        </button>
                        <div>
                            <h2 className="text-2xl font-bold text-primary mb-4">
                                <i className="fas fa-shield-alt mr-2"></i>
                                {emailChangeStep === 'old' ? 'Confirmez votre adresse actuelle' : 'Confirmez votre nouvelle adresse'}
                            </h2>
                            <p className="text-gray-700 mb-2">{emailChangeMessage}</p>
                            <p className="text-sm text-gray-500 mb-4">
                                {emailChangeStep === 'old'
                                    ? `Code envoyé à ${user.username}. Votre compte ne changera pas tant que les deux codes n'auront pas été validés.`
                                    : `Code envoyé à ${emailChangePending}.`}
                            </p>
                            {emailChangeError && <div className="alert alert-error mb-4">{emailChangeError}</div>}
                            <form onSubmit={handleVerifyEmailCode} className="space-y-4">
                                <div className="form-group">
                                    <label className="form-label">Code à 6 chiffres</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        maxLength={6}
                                        value={emailChangeCode}
                                        onChange={(e) => setEmailChangeCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        className="form-input tracking-widest text-center text-xl"
                                        required
                                    />
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                                    <button
                                        type="button"
                                        onClick={handleCancelEmailChange}
                                        className="btn-secondary"
                                        disabled={emailChangeLoading}
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleResendEmailCode}
                                        className="btn-secondary"
                                        disabled={emailChangeLoading}
                                    >
                                        Renvoyer le code
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={emailChangeLoading || emailChangeCode.length !== 6}
                                    >
                                        {emailChangeLoading ? 'Vérification...' : 'Valider le code'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;
